import type { DataTableFilterField } from '@/components/data-table/types'
import { ControlsProvider } from '@/components/controls'
import type {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  SortingState,
  Table,
  VisibilityState,
} from '@tanstack/react-table'
import { createContext, useContext, useMemo } from 'react'
import { DataTableStoreSync } from './data-table-store-sync'

// REMINDER: read about how to move controlled state out of the useReactTable hook
// https://github.com/TanStack/table/discussions/4005#discussioncomment-7303569

interface DataTableStateContextType {
  columnFilters: ColumnFiltersState
  sorting: SortingState
  rowSelection: RowSelectionState
  columnOrder: string[]
  columnVisibility: VisibilityState
  pagination: PaginationState
  enableColumnOrdering: boolean
}

interface DataTableBaseContextType<TData = unknown, TValue = unknown> {
  table: Table<TData>
  filterFields: DataTableFilterField<TData>[]
  columns: ColumnDef<TData, TValue>[]
  isLoading?: boolean
  totalRows?: number
  getFacetedUniqueValues?: (
    table: Table<TData>,
    columnId: string
  ) => Map<string, number>
  getFacetedMinMaxValues?: (
    table: Table<TData>,
    columnId: string
  ) => undefined | [number, number]
}

interface DataTableContextType<TData = unknown, TValue = unknown>
  extends DataTableStateContextType,
    DataTableBaseContextType<TData, TValue> {}

export const DataTableContext = createContext<DataTableContextType<
  any,
  any
> | null>(null)

export function DataTableProvider<TData, TValue>({
  children,
  initialControlsOpen,
  ...props
}: Partial<DataTableStateContextType> &
  DataTableBaseContextType<TData, TValue> & {
    children: React.ReactNode
    initialControlsOpen?: boolean
  }) {
  const value = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- openstatus registry
    () => ({
      ...props,
      columnFilters: props.columnFilters ?? [],
      sorting: props.sorting ?? [],
      rowSelection: props.rowSelection ?? {},
      columnOrder: props.columnOrder ?? [],
      columnVisibility: props.columnVisibility ?? {},
      pagination: props.pagination ?? { pageIndex: 0, pageSize: 10 },
      enableColumnOrdering: props.enableColumnOrdering ?? false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openstatus registry
    [
      props.columnFilters,
      props.sorting,
      props.rowSelection,
      props.columnOrder,
      props.columnVisibility,
      props.pagination,
      props.table,
      props.filterFields,
      props.columns,
      props.enableColumnOrdering,
      props.isLoading,
      props.totalRows,
      props.getFacetedUniqueValues,
      props.getFacetedMinMaxValues,
    ]
  )

  return (
    <DataTableContext.Provider value={value}>
      <ControlsProvider initialOpen={initialControlsOpen}>
        <DataTableStoreSync>{children}</DataTableStoreSync>
      </ControlsProvider>
    </DataTableContext.Provider>
  )
}

export function useDataTable<TData, TValue>() {
  const context = useContext(DataTableContext)

  if (!context) {
    throw new Error('useDataTable must be used within a DataTableProvider')
  }

  return context as DataTableContextType<TData, TValue>
}
